import { AccountItem } from '@/lib/auth/core/account'
import { IMediaItem, MediaDimensions } from '@/lib/types/media/core'
import { StoreMediaMetadata } from '@/lib/types/media/metadata'
import { StoreMediaItem } from '@/lib/types/media/store'
import { PaymentProfileItem } from '@/lib/types/operations/payment-method'
import { UserAddressItem, UserItem, UserProfileItem } from '@/lib/types/user'
import { MediaPlacement } from '@prisma/client'
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit'

// Define a store-specific media item that includes additional UI state
type SerializedMediaItem = IMediaItem

// Define writable media item for Redux reducers
type WritableMediaItem = Draft<StoreMediaItem>

/**
 * Adapter function to convert IMediaItem to StoreMediaItem for Redux state
 * This ensures type compatibility and handles null/undefined values properly
 */
function adaptToStoreMedia(mediaItem: IMediaItem): any {
  // Ensure dimensions are properly formatted
  const dimensions: MediaDimensions = {
    width: typeof mediaItem.metadata?.dimensions?.width === 'number' ? mediaItem.metadata.dimensions.width : 0,
    height: typeof mediaItem.metadata?.dimensions?.height === 'number' ? mediaItem.metadata.dimensions.height : 0,
    aspectRatio:
      typeof mediaItem.metadata?.dimensions?.aspectRatio === 'number' ? mediaItem.metadata.dimensions.aspectRatio : 1
  }

  // Create properly formatted metadata
  const metadata = {
    alt: mediaItem.metadata?.alt || '',
    ownerId: mediaItem.metadata?.ownerId || '',
    mimeType:
      typeof mediaItem.metadata?.mimeType === 'string' ? mediaItem.metadata.mimeType : 'application/octet-stream',
    dimensions,
    thumbnailUrl: mediaItem.metadata?.thumbnailUrl,
    duration: mediaItem.metadata?.duration,
    description: mediaItem.metadata?.description,
    customPath: mediaItem.metadata?.customPath,
    placement: mediaItem.metadata?.placement || mediaItem.placement || MediaPlacement.OTHER,
    size: mediaItem.metadata?.size || mediaItem.size || 0,
    caption: mediaItem.caption || '',
    orderIndex: typeof mediaItem.metadata?.orderIndex === 'number' ? mediaItem.metadata.orderIndex : 0,
    originalName: null
  }

  // Return a properly formatted media item for Redux store
  return {
    id: mediaItem.id,
    url: mediaItem.url,
    placement: mediaItem.placement,
    type: mediaItem.type,
    mimeType: mediaItem.mimeType || 'application/octet-stream',
    size: mediaItem.size || 0,
    metadata,
    createdAt: typeof mediaItem.createdAt === 'string' ? mediaItem.createdAt : new Date().toISOString(),
    updatedAt: typeof mediaItem.updatedAt === 'string' ? mediaItem.updatedAt : new Date().toISOString(),
    altText: mediaItem.altText || '',
    caption: mediaItem.caption || ''
  }
}

// Helper function to safely serialize a potential date value
function serializeDateValue(value: any): string | null | undefined {
  if (value === null || value === undefined) {
    return value
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  // If it's already a string or some other value, return as is
  return value
}

// Helper function to ensure metadata.alt is never null
function ensureValidMedia(media: IMediaItem) {
  return {
    ...media,
    metadata: {
      ...media.metadata,
      alt: media.metadata?.alt || '', // Ensure alt is never null
      mimeType: media.metadata?.mimeType || 'application/octet-stream' // Ensure mimeType is never null
    }
  }
}

interface MediaUploadState {
  isUploading: boolean
  progress: number
  uploadId?: string
  error?: string | null
  lastUploadedMedia?: StoreMediaItem | null
}

// Define valid modal placements
const MODAL_PLACEMENTS = [MediaPlacement.AVATAR, MediaPlacement.FEATURED_MEDIA] as const
type ModalPlacement = (typeof MODAL_PLACEMENTS)[number]

interface ModalState extends Record<ModalPlacement, boolean> {
  placement: ModalPlacement
}

interface UserProfileState {
  userProfile:
    | (UserProfileItem & {
        media: StoreMediaItem[] // Always an array, never null
        user?: UserItem | null
        accounts?: AccountItem[] | null
      })
    | null
  userAddresses: UserAddressItem[]
  userPaymentProfiles: PaymentProfileItem[]
  initialized: boolean
  loading: boolean
  error: string | null
  isEmpty?: boolean
  mediaState: Record<MediaPlacement, MediaUploadState>
  modalState: ModalState | null
  media: IMediaItem[]
}

const initialUserProfile: UserProfileState = {
  userProfile: null,
  userAddresses: [],
  userPaymentProfiles: [],
  initialized: false,
  loading: false,
  error: null,
  mediaState: Object.values(MediaPlacement).reduce(
    (acc, placement) => ({
      ...acc,
      [placement]: {
        isUploading: false,
        progress: 0,
        uploadId: undefined,
        error: null,
        lastUploadedMedia: null
      }
    }),
    {} as Record<MediaPlacement, MediaUploadState>
  ),
  modalState: null,
  media: []
}

const profileSlice = createSlice({
  name: 'profile',
  initialState: initialUserProfile,
  reducers: {
    setUserProfile: (state, action: PayloadAction<UserProfileItem & { media?: any[] }>) => {
      // Always default to [] if media is missing
      const existingMedia = (state.userProfile?.media ?? []) as IMediaItem[]
      const payloadMedia = action.payload.media ?? []
      const hasIncomingMedia = Array.isArray(payloadMedia) && payloadMedia.length > 0

      // ALWAYS preserve existing featured media and avatar if they exist
      const existingFeaturedMedia = existingMedia.find(
        (m: IMediaItem) => m.placement === MediaPlacement.FEATURED_MEDIA && !m.isDeleted
      )

      const existingAvatar = existingMedia.find(
        (m: IMediaItem) => m.placement === MediaPlacement.AVATAR && !m.isDeleted
      )

      // Process incoming media if available
      const processedMedia = hasIncomingMedia
        ? payloadMedia.map((item) => {
            const dimensions: MediaDimensions = {
              width: item.metadata?.dimensions?.width || 0,
              height: item.metadata?.dimensions?.height || 0,
              aspectRatio: item.metadata?.dimensions?.aspectRatio || 1
            }

            const metadata: StoreMediaMetadata = {
              alt: item.metadata?.alt || '',
              ownerId: item.metadata?.ownerId || '',
              mimeType:
                typeof item.metadata?.mimeType === 'string' && item.metadata?.mimeType
                  ? item.metadata.mimeType
                  : 'application/octet-stream',
              dimensions,
              thumbnailUrl: item.metadata?.thumbnailUrl,
              duration: item.metadata?.duration,
              description: item.metadata?.description,
              customPath: item.metadata?.customPath,
              placement: item.placement || MediaPlacement.OTHER,
              size: item.size || 0,
              caption: item.caption || '',
              orderIndex: item.metadata?.orderIndex || 0,
              originalName: null
            }

            // Guarantee metadata.mimeType is always a string
            metadata.mimeType = metadata.mimeType || 'application/octet-stream'
            return {
              ...item,
              metadata,
              mimeType: metadata.mimeType || 'application/octet-stream'
            }
          })
        : []

      // Merge existing media with incoming media, prioritizing existing featured media and avatar
      let finalMedia: IMediaItem[] = []

      // First, add all incoming media except featured media and avatar
      if (hasIncomingMedia) {
        finalMedia = processedMedia.filter(
          (m: IMediaItem) => m.placement !== MediaPlacement.FEATURED_MEDIA && m.placement !== MediaPlacement.AVATAR
        )
      }

      // Then add existing media that's not in the incoming media
      existingMedia.forEach((existingItem: IMediaItem) => {
        // Skip if already in finalMedia
        if (finalMedia.some((m: IMediaItem) => m.id === existingItem.id)) {
          return
        }

        // Add to finalMedia
        finalMedia.push(existingItem)
      })

      // Always prioritize existing featured media and avatar if they exist
      if (existingFeaturedMedia) {
        // Remove any other featured media first
        finalMedia = finalMedia.filter((m: IMediaItem) => m.placement !== MediaPlacement.FEATURED_MEDIA)

        // Add the existing featured media
        finalMedia.push(existingFeaturedMedia)
      } else if (hasIncomingMedia) {
        // If no existing featured media, add from incoming if available
        const incomingFeaturedMedia = processedMedia.find(
          (m: IMediaItem) => m.placement === MediaPlacement.FEATURED_MEDIA && !m.isDeleted
        )

        if (incomingFeaturedMedia) {
          finalMedia.push(incomingFeaturedMedia)
        }
      }

      if (existingAvatar) {
        // Remove any other avatar first
        finalMedia = finalMedia.filter((m: IMediaItem) => m.placement !== MediaPlacement.AVATAR)

        // Add the existing avatar
        finalMedia.push(existingAvatar)
      } else if (hasIncomingMedia) {
        // If no existing avatar, add from incoming if available
        const incomingAvatar = processedMedia.find(
          (m: IMediaItem) => m.placement === MediaPlacement.AVATAR && !m.isDeleted
        )

        if (incomingAvatar) {
          finalMedia.push(incomingAvatar)
        }
      }

      // Process media items to ensure they match the StoreMediaItem type
      const processedMediaItems = finalMedia.map((item) => adaptToStoreMedia(ensureValidMedia(item)))

      // Serialize all date objects in user data
      const userWithSerializedDates = action.payload.user
        ? {
            ...action.payload.user,
            // Ensure all date fields are serialized
            lastSignIn: serializeDateValue(action.payload.user.lastSignIn),
            tosAcceptedAt: serializeDateValue(action.payload.user.tosAcceptedAt),
            createdAt: serializeDateValue(action.payload.user.createdAt),
            updatedAt: serializeDateValue(action.payload.user.updatedAt),
            emailVerified: serializeDateValue(action.payload.user.emailVerified),
            phoneVerified: serializeDateValue(action.payload.user.phoneVerified)
          }
        : null

      // No need for double-check as serializeDateValue handles all cases properly

      state.userProfile = {
        ...action.payload,
        user: userWithSerializedDates as any,
        media: processedMediaItems as any
      }
      state.isEmpty = false

      // Also update the media array for consistency
      state.media = processedMediaItems as any
    },
    setProfileTitle: (state, action: PayloadAction<string>) => {
      if (!state.userProfile) return
      state.userProfile.title = action.payload
    },
    setProfileShopLocation: (state, action: PayloadAction<string>) => {
      if (!state.userProfile) return
      state.userProfile.shopLocation = action.payload
    },
    setProfileBio: (state, action: PayloadAction<string>) => {
      if (!state.userProfile) return
      state.userProfile.bio = action.payload
    },
    setUserAddresses: (state, action: PayloadAction<UserAddressItem[]>) => {
      state.userAddresses = action.payload
      state.isEmpty = action.payload.length === 0
    },
    setUserPaymentProfiles: (state, action: PayloadAction<PaymentProfileItem[]>) => {
      // Serialize any Date objects to ISO strings for Redux serialization
      const serializedPaymentProfiles = action.payload.map((profile) => ({
        ...profile,
        createdAt: serializeDateValue(profile.createdAt),
        updatedAt: serializeDateValue(profile.updatedAt)
      }))

      state.userPaymentProfiles = serializedPaymentProfiles as PaymentProfileItem[]
      state.isEmpty = action.payload.length === 0
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    updateUserMedia: (state, action: PayloadAction<IMediaItem>) => {
      if (!state.userProfile?.media) return

      const media = action.payload
      const processedMedia = adaptToStoreMedia(ensureValidMedia(media))

      const updatedMedia = [...state.userProfile.media]
      const existingIndex = updatedMedia.findIndex((m) => m.id === media.id)

      if (existingIndex !== -1) {
        updatedMedia[existingIndex] = {
          ...processedMedia,
          isDeleted: false
        }
      } else {
        updatedMedia.push({
          ...processedMedia,
          isDeleted: false
        })
      }

      state.userProfile.media = updatedMedia
    },
    updateUserName: (state, action: PayloadAction<string>) => {
      if (state.userProfile?.user) {
        state.userProfile.user.name = action.payload
      }
    },
    updateUserPhone: (state, action: PayloadAction<string>) => {
      if (state.userProfile?.user) {
        state.userProfile.user.phone = action.payload
      }
    },
    updateUserEmail: (state, action: PayloadAction<string>) => {
      if (state.userProfile?.user) {
        state.userProfile.user.email = action.payload
      }
    },
    updateUserOAuthAccountProviders: (state, action: PayloadAction<AccountItem[]>) => {
      if (!state.userProfile) return
      state.userProfile.accounts = action.payload
    },
    verifyUserEmail: (state, action: PayloadAction<string>) => {
      if (state.userProfile?.user) {
        state.userProfile.user.emailVerified = action.payload
      }
    },
    verifyUserPhone: (state, action: PayloadAction<string>) => {
      if (state.userProfile?.user) {
        state.userProfile.user.phoneVerified = action.payload
      }
    },
    setIsUploading: (
      state,
      action: PayloadAction<{ placement: MediaPlacement; isUploading: boolean; uploadId?: string }>
    ) => {
      const { placement, isUploading, uploadId } = action.payload
      const currentState = state.mediaState[placement]

      if (!currentState.uploadId || currentState.uploadId === uploadId) {
        state.mediaState[placement] = {
          ...currentState,
          isUploading,
          uploadId: isUploading ? uploadId : undefined
        }
      }
    },
    setUploadProgress: (
      state,
      action: PayloadAction<{ placement: MediaPlacement; progress: number; uploadId?: string }>
    ) => {
      const { placement, progress, uploadId } = action.payload
      const currentState = state.mediaState[placement]

      if (!currentState.uploadId || currentState.uploadId === uploadId) {
        state.mediaState[placement] = {
          ...currentState,
          progress
        }
      }
    },
    setModalState: (state, action: PayloadAction<{ placement: ModalPlacement; isOpen: boolean }>) => {
      const { placement, isOpen } = action.payload
      if (!state.modalState) {
        state.modalState = {
          [MediaPlacement.AVATAR]: false,
          [MediaPlacement.FEATURED_MEDIA]: false,
          placement
        } as ModalState
      }
      if (MODAL_PLACEMENTS.includes(placement)) {
        state.modalState[placement] = isOpen
        state.modalState.placement = placement
      }
    },
    closeAllModals: (state) => {
      if (state.modalState) {
        MODAL_PLACEMENTS.forEach((placement) => {
          state.modalState![placement] = false
        })
      }
    },
    startMediaUpload: (state, action: PayloadAction<{ placement: MediaPlacement; uploadId: string }>) => {
      const { placement, uploadId } = action.payload
      state.mediaState[placement] = {
        ...state.mediaState[placement],
        isUploading: true,
        progress: 0,
        uploadId,
        error: null
      }
    },
    updateMediaUploadProgress: (state, action: PayloadAction<{ placement: MediaPlacement; progress: number }>) => {
      const { placement, progress } = action.payload
      if (state.mediaState[placement]) {
        state.mediaState[placement].progress = progress
      }
    },
    completeMediaUpload: (state, action: PayloadAction<{ placement: MediaPlacement; media: IMediaItem }>) => {
      const { placement, media } = action.payload

      if (!state.userProfile) return

      const updatedMedia = [...(state.userProfile.media || [])]

      if (placement === MediaPlacement.AVATAR || placement === MediaPlacement.FEATURED_MEDIA) {
        updatedMedia.forEach((item, index) => {
          if (item.placement === placement && !item.isDeleted) {
            updatedMedia[index] = { ...item, isDeleted: true }
          }
        })

        const existingIndex = updatedMedia.findIndex((m) => m.id === media.id)
        if (existingIndex !== -1) {
          updatedMedia[existingIndex] = {
            ...adaptToStoreMedia(ensureValidMedia(media)),
            placement,
            isDeleted: false
          }
        } else {
          updatedMedia.push({
            ...adaptToStoreMedia(ensureValidMedia(media)),
            placement,
            isDeleted: false
          })
        }

        state.mediaState[placement] = {
          ...state.mediaState[placement],
          isUploading: false,
          progress: 100,
          error: null,
          lastUploadedMedia: adaptToStoreMedia(media)
        }
      } else {
        const existingIndex = updatedMedia.findIndex((m) => m.id === media.id)
        if (existingIndex !== -1) {
          updatedMedia[existingIndex] = adaptToStoreMedia(ensureValidMedia(media))
        } else {
          updatedMedia.push(adaptToStoreMedia(ensureValidMedia(media)))
        }
      }

      state.userProfile.media = updatedMedia
      state.media = [...updatedMedia]
    },
    setMediaUploadError: (state, action: PayloadAction<{ placement: MediaPlacement; error: string }>) => {
      const { placement, error } = action.payload
      state.mediaState[placement] = {
        ...state.mediaState[placement],
        isUploading: false,
        error,
        uploadId: undefined
      }
    },
    resetMediaState: (state, action: PayloadAction<MediaPlacement>) => {
      const placement = action.payload
      state.mediaState[placement] = {
        isUploading: false,
        progress: 0,
        uploadId: undefined,
        error: null,
        lastUploadedMedia: null
      }
    },
    addMediaItem: (state, action: PayloadAction<IMediaItem>) => {
      if (!state.userProfile) return

      const media = action.payload
      const processedMedia = adaptToStoreMedia(ensureValidMedia(media))

      const updatedMedia = [...(state.userProfile.media || [])]
      const existingIndex = updatedMedia.findIndex((m) => m.id === media.id)

      if (existingIndex !== -1) {
        // Update existing item
        updatedMedia[existingIndex] = processedMedia
      } else {
        // Add new item
        updatedMedia.push(processedMedia)
      }

      // Update media in the profile
      if (state.userProfile) {
        state.userProfile.media = updatedMedia
      }

      // Also update the main media array
      state.media = [...state.media, processedMedia]
    },
    updatePlacementMedia: (state, action: PayloadAction<{ media: IMediaItem; placement: MediaPlacement }>) => {
      const { media, placement } = action.payload
      const processedMedia = adaptToStoreMedia(ensureValidMedia(media))

      if (!state.userProfile?.media) return

      const updatedMedia = [...state.userProfile.media]

      const existingIndex = updatedMedia.findIndex((m) => m.id === media.id)

      if (existingIndex !== -1) {
        // Update existing item
        updatedMedia[existingIndex] = {
          ...processedMedia,
          placement,
          isDeleted: false // Ensure it's not marked as deleted
        }
      } else {
        // Add new item
        updatedMedia.push({
          ...processedMedia,
          placement,
          isDeleted: false // Ensure it's not marked as deleted
        })
        if (placement === MediaPlacement.AVATAR) {
          console.log(`✅ Added new avatar to state: ${media.id}`)
        }
      }

      // Update the media array
      state.userProfile.media = updatedMedia
    },
    setMedia: (state, action: PayloadAction<IMediaItem[]>) => {
      if (!state.userProfile) return
      const processedMedia = action.payload.map((item) => adaptToStoreMedia(ensureValidMedia(item)))

      // Update all media
      state.media = [...processedMedia]

      // Also update the profile's media
      if (state.userProfile) {
        const updatedMedia = [...(state.userProfile.media || [])]
        for (const media of processedMedia) {
          const existingIndex = updatedMedia.findIndex((m) => m.id === media.id)
          if (existingIndex !== -1) {
            updatedMedia[existingIndex] = media
          } else {
            updatedMedia.push(media)
          }
        }
        state.userProfile.media = updatedMedia
      }
    },
    revertUserMedia: (state, action: PayloadAction<{ placement: MediaPlacement; media: IMediaItem | null }>) => {
      const { placement } = action.payload

      if (!state.userProfile) return

      const updatedMedia = [...(state.userProfile.media || [])]

      if (placement === MediaPlacement.AVATAR || placement === MediaPlacement.FEATURED_MEDIA) {
        updatedMedia.forEach((item, index) => {
          if (item.placement === placement) {
            updatedMedia[index] = { ...item, isDeleted: true }
          }
        })
      }

      state.userProfile.media = updatedMedia
      state.media = [...updatedMedia]
    }
  }
})

export const {
  setUserProfile,
  setUserAddresses,
  setUserPaymentProfiles,
  setInitialized,
  setLoading,
  setError,
  updateUserPhone,
  setProfileTitle,
  setProfileShopLocation,
  setProfileBio,
  updateUserEmail,
  verifyUserEmail,
  verifyUserPhone,
  setIsUploading,
  setUploadProgress,
  updateUserMedia,
  updateUserName,
  revertUserMedia,
  setModalState,
  closeAllModals,
  updateUserOAuthAccountProviders,
  startMediaUpload,
  updateMediaUploadProgress,
  completeMediaUpload,
  setMediaUploadError,
  resetMediaState,
  addMediaItem,
  updatePlacementMedia,
  setMedia
} = profileSlice.actions

export const userProfileReducer = profileSlice.reducer

export type { UserProfileState }

export type UserProfileActions = typeof profileSlice.actions
